import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, Trash2, Loader, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import notificationService from '../../services/notificationService';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const SimpleNotificationPreferences = ({ embedded = false }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const subs = await notificationService.getSubscriptions();
      // Only show enabled subscriptions
      const enabledSubs = subs.filter(sub => sub.enabled);
      setSubscriptions(enabledSubs);
      
      // Expand all groups by default
      const groups = groupSubscriptions(enabledSubs);
      const expanded = {};
      Object.keys(groups).forEach(key => {
        expanded[key] = true;
      });
      setExpandedGroups(expanded);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const groupSubscriptions = (subs) => {
    const grouped = {};
    
    subs.forEach(sub => {
      let groupKey;
      let groupLabel;
      
      if (sub.study_id && sub.site_id) {
        groupKey = `study_${sub.study_id}_site_${sub.site_id}`;
        groupLabel = `${sub.study_name || 'Study'} - ${sub.site_name || 'Site'}`;
      } else if (sub.study_id) {
        groupKey = `study_${sub.study_id}`;
        groupLabel = sub.study_name || `Study ${sub.study_id}`;
      } else if (sub.site_id) {
        groupKey = `site_${sub.site_id}`;
        groupLabel = sub.site_name || `Site ${sub.site_id}`;
      } else {
        groupKey = 'global';
        groupLabel = 'All Studies';
      }
      
      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          label: groupLabel,
          studyId: sub.study_id,
          siteId: sub.site_id,
          subscriptions: []
        };
      }
      
      grouped[groupKey].subscriptions.push(sub);
    });
    
    return grouped;
  };

  const toggleSubscription = async (subscriptionId, currentState) => {
    // Optimistic update
    setSubscriptions(prev => 
      prev.map(sub => 
        sub.id === subscriptionId 
          ? { ...sub, enabled: !currentState, in_app_enabled: !currentState }
          : sub
      )
    );

    try {
      await notificationService.updateSubscription(subscriptionId, {
        enabled: !currentState,
        in_app_enabled: !currentState
      });
      toast.success(currentState ? 'Notification disabled' : 'Notification enabled');
    } catch (error) {
      console.error('Failed to toggle subscription:', error);
      // Revert on error
      setSubscriptions(prev => 
        prev.map(sub => 
          sub.id === subscriptionId 
            ? { ...sub, enabled: currentState, in_app_enabled: currentState }
            : sub
        )
      );
      toast.error('Failed to update notification');
    }
  };

  const deleteSubscription = async (subscriptionId) => {
    const subToDelete = subscriptions.find(s => s.id === subscriptionId);
    
    // Optimistic delete
    setSubscriptions(prev => prev.filter(sub => sub.id !== subscriptionId));
    
    // Show toast with undo option
    const toastId = toast.success(
      (t) => (
        <div className="flex items-center justify-between gap-3">
          <span>Notification removed</span>
          <button
            onClick={() => {
              // Restore the subscription
              setSubscriptions(prev => [...prev, subToDelete]);
              toast.dismiss(t.id);
              toast.success('Notification restored');
            }}
            className="bg-white text-primary-600 px-3 py-1 rounded font-medium hover:bg-primary-50 transition-colors"
          >
            Undo
          </button>
        </div>
      ),
      { duration: 5000 }
    );
    
    try {
      await notificationService.deleteSubscription(subscriptionId);
    } catch (error) {
      console.error('Failed to delete subscription:', error);
      // Restore on error
      setSubscriptions(prev => [...prev, subToDelete]);
      toast.dismiss(toastId);
      toast.error('Failed to remove notification');
    }
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const getEventDisplayName = (eventKey) => {
    const names = {
      'survey.missed': 'Missed Surveys',
      'survey.completed': 'Survey Completions',
      'participant.created': 'New Participants',
      'participant.activated': 'Participant Activations',
      'participant.deactivated': 'Participant Deactivations',
      'dcq.created': 'New Data Queries',
      'dcq.updated': 'Data Query Updates'
    };
    return names[eventKey] || eventKey.replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getEventIcon = (eventKey) => {
    if (eventKey.startsWith('survey.')) return '📋';
    if (eventKey.startsWith('participant.')) return '👤';
    if (eventKey.startsWith('dcq.')) return '📝';
    return '🔔';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  const groupedSubs = groupSubscriptions(subscriptions);
  const hasSubscriptions = Object.keys(groupedSubs).length > 0;

  return (
    <div className={embedded ? "" : "max-w-4xl mx-auto"}>
      <div className={embedded ? "" : "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"}>
        {/* Header */}
        <div className={embedded ? "mb-6" : "bg-gradient-to-r from-primary-50 to-blue-50 dark:from-gray-900 dark:to-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700"}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!embedded && (
                <div className="bg-white dark:bg-gray-700 p-2 rounded-lg shadow-sm">
                  <Bell className="h-5 w-5 text-primary-600" />
                </div>
              )}
              <div>
                <h2 className={embedded ? "text-xl font-semibold text-gray-900" : "text-xl font-semibold text-gray-900"}>Notification Preferences</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {hasSubscriptions 
                    ? `${subscriptions.length} active notification${subscriptions.length === 1 ? '' : 's'}`
                    : 'Manage your notification settings'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={embedded ? "" : "p-6"}>
          {!hasSubscriptions ? (
            <div className="text-center py-8">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Bell className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Active Notifications</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Set up notifications for your studies to stay informed about important events.
              </p>
              <p className="text-sm text-gray-500">
                Visit a <Link to="/studies" className="text-primary-600 hover:underline font-medium">study page</Link> and 
                go to the Notifications tab to enable alerts.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedSubs).map(([groupKey, group]) => (
                <div key={groupKey} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full bg-gray-50 px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {expandedGroups[groupKey] ? (
                        <ChevronDown className="h-4 w-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-600" />
                      )}
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{group.label}</span>
                      <span className="text-sm text-gray-500">
                        ({group.subscriptions.length} notification{group.subscriptions.length === 1 ? '' : 's'})
                      </span>
                    </div>
                    {group.studyId && (
                      <Link
                        to={group.siteId 
                          ? `/studies/${group.studyId}/sites/${group.siteId}` 
                          : `/studies/${group.studyId}?tab=notifications`
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View →
                      </Link>
                    )}
                  </button>

                  {/* Group Content */}
                  {expandedGroups[groupKey] && (
                    <div className="divide-y divide-gray-100">
                      {group.subscriptions.map((sub) => (
                        <motion.div
                          key={sub.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-2xl">{getEventIcon(sub.event_type_key)}</span>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  {getEventDisplayName(sub.event_type_key)}
                                </div>
                                <div className="text-sm text-gray-500 mt-0.5">
                                  {sub.in_app_enabled && <span className="text-primary-600">In-App</span>}
                                  {sub.in_app_enabled && sub.email_enabled && <span className="text-gray-400"> • </span>}
                                  {sub.email_enabled && <span className="text-blue-600">Email</span>}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              {/* Toggle */}
                              <button
                                onClick={() => toggleSubscription(sub.id, sub.enabled)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                  sub.enabled ? 'bg-primary-600' : 'bg-gray-200'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    sub.enabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => deleteSubscription(sub.id)}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                title="Remove notification"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={embedded ? "mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700" : "bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700"}>
          <div className="flex items-start gap-2">
            <span className="text-lg">💡</span>
            <div className="flex-1">
              <p className="text-sm text-gray-700 font-medium mb-1">How to Add Notifications</p>
              <p className="text-xs text-gray-600">
                Visit any <Link to="/studies" className="text-primary-600 hover:underline font-medium">study page</Link> and 
                click the <strong>Notifications</strong> tab to enable study-specific alerts. You can also enable 
                site-specific notifications from individual site pages.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleNotificationPreferences;
